import { useEffect, useId, useRef } from "react";

// Shared accessible-dialog behaviour (#139). Both ui.jsx's Modal and
// CartModal.jsx use this single implementation, rather than each hand-rolling
// (or, previously, omitting) focus trapping. It only ever touches refs,
// event handlers, and ARIA/tabIndex attributes — never layout, color, size,
// or animation — so it cannot change how any dialog looks. Pulled into its
// own module (rather than living inside ui.jsx, which otherwise exports only
// components) the same way pushNotificationRules.js sits next to
// OrderNotificationsSection.jsx: pure logic, directly testable without a DOM.

// Elements a keyboard user can land on inside a dialog. Kept deliberately
// simple (no visibility/geometry probing) since every current dialog's
// interactive content is always visible whenever the dialog itself is open.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=\"hidden\"])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex=\"-1\"])",
].join(",");

export function getFocusableElements(container) {
  if (!container || typeof container.querySelectorAll !== "function") return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter(el => el.getAttribute("aria-hidden") !== "true");
}

// Pure decision: given the dialog's focusable elements, where focus currently
// sits, and whether Shift is held, should this Tab press be intercepted (and
// if so, to where)? Kept separate from any DOM/event code so it is testable
// with plain objects.
export function getFocusTrapTarget({ focusables, activeElement, shiftKey }) {
  if (!focusables || focusables.length === 0) return { trap: true, target: null };
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const idx = focusables.indexOf(activeElement);
  if (shiftKey) {
    if (idx <= 0) return { trap: true, target: last };
  } else if (idx === -1 || idx === focusables.length - 1) {
    return { trap: true, target: first };
  }
  return { trap: false, target: null };
}

// Hides everything behind `node` from assistive tech by walking up to `root`
// (default document.body) and marking every sibling at every level
// aria-hidden, restoring each one's prior aria-hidden value. Framework/DOM
// agnostic (only uses parentElement/children/get|set|removeAttribute) so it
// is testable against plain stub objects.
export function hideOthers(node, root) {
  const stopAt = root || (typeof document !== "undefined" ? document.body : null);
  const hidden = [];
  let current = node;
  while (current && current !== stopAt && current.parentElement) {
    const parent = current.parentElement;
    const siblings = parent.children ? Array.from(parent.children) : [];
    for (const sibling of siblings) {
      if (sibling === current) continue;
      if (sibling.getAttribute && sibling.getAttribute("data-dialog-a11y-hidden") === "true") continue;
      const prev = sibling.getAttribute ? sibling.getAttribute("aria-hidden") : null;
      hidden.push({ el: sibling, prev });
      if (sibling.setAttribute) {
        sibling.setAttribute("aria-hidden", "true");
        sibling.setAttribute("data-dialog-a11y-hidden", "true");
      }
    }
    current = parent;
  }
  return function restore() {
    for (const { el, prev } of hidden) {
      if (!el.setAttribute) continue;
      if (prev === null || prev === undefined) el.removeAttribute("aria-hidden");
      else el.setAttribute("aria-hidden", prev);
      if (el.removeAttribute) el.removeAttribute("data-dialog-a11y-hidden");
    }
  };
}

// The dialog primitive itself: focus moves in on open, Tab/Shift+Tab cycle
// within the dialog, Escape closes it, focus is restored to whatever had it
// before opening (even if the component unmounts while still open, since the
// effect cleanup below always runs on unmount too), and background content
// is hidden from assistive tech for the duration. Consumers spread
// `dialogProps` onto their dialog container element, attach `ref` to that
// same element, and wire `onKeyDown` — no other markup or styling required.
export function useDialogA11y({ open, onClose, labelledBy, label }) {
  const containerRef = useRef(null);
  const autoTitleId = useId();
  const titleId = labelledBy || autoTitleId;

  useEffect(() => {
    if (!open) return undefined;
    const container = containerRef.current;
    const previouslyFocused = typeof document !== "undefined" ? document.activeElement : null;
    const restoreInert = container ? hideOthers(container) : null;
    const focusables = getFocusableElements(container);
    const initial = focusables[0] || container;
    if (initial && typeof initial.focus === "function") initial.focus();

    return () => {
      if (restoreInert) restoreInert();
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose?.();
      return;
    }
    if (e.key !== "Tab") return;
    const container = containerRef.current;
    if (!container) return;
    const focusables = getFocusableElements(container);
    const action = getFocusTrapTarget({ focusables, activeElement: document.activeElement, shiftKey: e.shiftKey });
    if (action.trap) {
      e.preventDefault();
      const target = action.target || container;
      if (typeof target.focus === "function") target.focus();
    }
  };

  return {
    ref: containerRef,
    onKeyDown,
    titleId,
    dialogProps: {
      role: "dialog",
      "aria-modal": true,
      tabIndex: -1,
      ...(label ? { "aria-label": label } : { "aria-labelledby": titleId }),
    },
  };
}
